import React from "react";
import { ArrowUpRight } from "lucide-react";
import WhatsAppButton from "./Components/WhatsAppButton";

const projects = [
  {
    title: "Smart Home Automation",
    category: "IoT Solution",
    description:
      "An IoT-based smart home system designed to remotely manage lighting, temperature, and security through connected devices.",
    image: "/images/smart-home.jpg",
    link: "#",
    type: "Client Project",
  },
  {
    title: "AI Chatbot",
    category: "Artificial Intelligence",
    description:
      "An AI-powered customer support assistant built to provide instant responses, automate common inquiries, and improve customer engagement.",
    image: "/images/ai-chatbot.jpg",
    link: "#",
    type: "Client Project",
  },
  {
    title: "E-commerce Platform",
    category: "Web Application",
    description:
      "A scalable online commerce platform with product management, secure payments, customer accounts, inventory tracking, and order management.",
    image: "/images/ecommerce.jpg",
    link: "#",
    type: "Client Project",
  },
  {
    title: "Blockchain Voting System",
    category: "Blockchain",
    description:
      "A secure digital voting platform designed around transparency, integrity, and verifiable election processes using blockchain technology.",
    image: "/images/blockchain.jpg",
    link: "#",
    type: "Research Project",
  },
];

const Projects = () => {
  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-hidden">

      {/* Hero Section */}
      <section className="relative bg-slate-950 text-white pt-28 pb-24 px-6 overflow-hidden">

        {/* Background Effects */}
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[150px]" />

        <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-[140px]" />

        {/* Grid */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-7">
            <span className="w-2 h-2 bg-blue-400 rounded-full" />
            Our Work
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight mb-7">
            Ideas transformed into
            <span className="block text-blue-400">
              real digital products.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
            Explore some of the digital platforms, intelligent systems, and
            technology solutions developed by Nexindi Tech for businesses,
            organizations, and real-world use cases.
          </p>
        </div>
      </section>

      {/* Projects Section */}
      <section className="relative py-24 px-6 bg-slate-50 overflow-hidden">

        <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-blue-100/70 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-7xl mx-auto">

          {/* Section Heading */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">

            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-sm font-semibold mb-5">
                <span className="w-2 h-2 bg-blue-600 rounded-full" />
                Selected Projects
              </div>

              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900">
                Technology built to solve
                <span className="text-blue-600"> meaningful problems.</span>
              </h2>
            </div>

            <p className="text-slate-600 text-lg max-w-md leading-relaxed">
              Each project combines practical engineering, thoughtful design,
              and technology chosen around the problem being solved.
            </p>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">

            {projects.map((project, index) => (
              <article
                key={index}
                className="group relative bg-white border border-slate-200 rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-900/10"
              >

                {/* Image */}
                <div className="relative h-[260px] md:h-[320px] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />

                  {/* Project Type */}
                  <div className="absolute top-5 left-5">
                    <span className="inline-flex px-3 py-1.5 rounded-full text-xs font-semibold bg-white/90 backdrop-blur-md text-slate-700">
                      {project.type}
                    </span>
                  </div>

                  {/* Project Number */}
                  <span className="absolute top-5 right-5 text-white/70 text-sm font-semibold">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  {/* Title on image */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <p className="text-blue-300 text-sm font-semibold mb-2">
                      {project.category}
                    </p>

                    <h2 className="text-2xl md:text-3xl font-bold text-white">
                      {project.title}
                    </h2>
                  </div>
                </div>

                {/* Content */}
                <div className="p-7 md:p-8">
                  <p className="text-slate-600 leading-relaxed mb-7">
                    {project.description}
                  </p>

                  <div className="flex items-center justify-between border-t border-slate-200 pt-6">

                    <a
                      href={project.link}
                      target={project.link !== "#" ? "_blank" : undefined}
                      rel={
                        project.link !== "#"
                          ? "noopener noreferrer"
                          : undefined
                      }
                      className="group/link inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700 transition"
                    >
                      View Project

                      <ArrowUpRight
                        size={17}
                        className="transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5"
                      />
                    </a>

                    <span className="text-xs text-slate-400 uppercase tracking-wider">
                      Nexindi Tech
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="px-6 py-24 bg-white">
        <div className="relative max-w-7xl mx-auto rounded-[2rem] bg-gradient-to-br from-blue-600 via-blue-700 to-slate-950 px-7 py-16 md:px-16 md:py-20 overflow-hidden text-white">

          <div className="absolute -top-32 -right-20 w-[420px] h-[420px] bg-cyan-300/15 rounded-full blur-[120px]" />

          <div className="absolute -bottom-32 -left-20 w-[420px] h-[420px] bg-blue-300/15 rounded-full blur-[120px]" />

          <div className="relative z-10 grid lg:grid-cols-[1fr_auto] gap-10 items-center">

            <div className="max-w-3xl">
              <p className="text-blue-200 text-sm font-semibold uppercase tracking-wider mb-4">
                Your Idea Could Be Next
              </p>

              <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-5">
                Have a project you want to bring to life?
              </h2>

              <p className="text-blue-100 text-lg leading-relaxed">
                From initial idea and product planning to development and
                deployment, Nexindi Tech can help turn your concept into a
                reliable digital product.
              </p>
            </div>

            <a
              href="/contact"
              className="group inline-flex items-center justify-center gap-3 bg-white text-blue-700 px-7 py-4 rounded-xl font-bold hover:bg-blue-50 transition-all duration-300 hover:-translate-y-1"
            >
              Start Your Project

              <span className="transition-transform duration-300 group-hover:translate-x-1">
                →
              </span>
            </a>
          </div>
        </div>
      </section>

      <WhatsAppButton />
    </div>
  );
};

export default Projects;